import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  AlertCircle,
  Info,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Clock,
  Database,
  Zap,
  Brain,
  Target,
  Activity,
  X,
  Settings,
  RefreshCw
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';

export type AlertType = 'critical' | 'warning' | 'info' | 'success' | 'prediction';
export type AlertCategory = 'performance' | 'system' | 'ai' | 'business' | 'maintenance';

interface Alert {
  id: string;
  type: AlertType;
  category: AlertCategory;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  timestamp: Date;
  dismissed: boolean;
  actionable: boolean;
  suggestions?: string[];
  metrics?: {
    current: number;
    threshold: number;
    unit: string;
  };
}

const SmartAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissedFilter, setDismissedFilter] = useState<boolean>(false);
  const [categoryFilter, setCategoryFilter] = useState<AlertCategory | 'all'>('all');
  const { addNotification, addToast } = useNotifications();

  // Generate intelligent alerts based on system analysis
  const generateSmartAlerts = (): Alert[] => {
    const now = new Date();
    
    return [
      {
        id: '1',
        type: 'critical',
        category: 'ai',
        title: 'انخفاض دقة نموذج YOLO',
        description: 'تم رصد انخفاض في دقة نموذج YOLO إلى 78% خلال آخر 24 ساعة، مما قد يؤثر على جودة استخراج المنتجات.',
        impact: 'high',
        timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000), // 2 hours ago
        dismissed: false,
        actionable: true,
        suggestions: [
          'إعادة تدريب النموذج باستخدام بيانات حديثة',
          'زيادة عتبة الثقة إلى 0.5',
          'مراجعة جودة الصور المرفوعة مؤخراً',
          'تحديث معاملات معالجة الصور'
        ],
        metrics: {
          current: 78,
          threshold: 85,
          unit: '%'
        }
      },
      {
        id: '2',
        type: 'warning',
        category: 'performance',
        title: 'ارتفاع وقت استجابة API',
        description: 'متوسط وقت استجابة API وصل إلى 1.2 ثانية، وهو أعلى من المعدل الطبيعي بنسبة 40%.',
        impact: 'medium',
        timestamp: new Date(now.getTime() - 30 * 60 * 1000), // 30 minutes ago
        dismissed: false,
        actionable: true,
        suggestions: [
          'تحسين استعلامات قاعدة البيانات',
          'إضافة فهرسة للجداول الرئيسية',
          'تنظيف ذاكرة التخزين المؤقت',
          'مراقبة استخدام الخادم'
        ],
        metrics: {
          current: 1200,
          threshold: 800,
          unit: 'ms'
        }
      },
      {
        id: '3',
        type: 'info',
        category: 'business',
        title: 'نمط جديد في بيانات العروض',
        description: 'تم رصد زيادة بنسبة 35% في عروض فئة "المواد الغذائية" مقارنة بالأسبوع الماضي.',
        impact: 'low',
        timestamp: new Date(now.getTime() - 60 * 60 * 1000), // 1 hour ago
        dismissed: false,
        actionable: false,
        suggestions: [
          'مراجعة استراتيجيات التسعير للمواد الغذائية',
          'تحسين خوارزميات الكشف لهذه الفئة',
          'إضافة فئات فرعية أكثر تخصصاً'
        ]
      },
      {
        id: '4',
        type: 'prediction',
        category: 'business',
        title: 'توقع زيادة في الحمل',
        description: 'بناءً على البيانات التاريخية، متوقع زيادة في معالجة العروض بنسبة 60% خلال الأسبوع القادم.',
        impact: 'medium',
        timestamp: new Date(now.getTime() - 10 * 60 * 1000), // 10 minutes ago
        dismissed: false,
        actionable: true,
        suggestions: [
          'تجهيز موارد إضافية للخادم',
          'تحسين خوارزميات المعالجة المتوازية',
          'إعداد نظام طوابير للمعالجة',
          'إشعار الفريق التقني للاستعداد'
        ]
      },
      {
        id: '5',
        type: 'warning',
        category: 'system',
        title: 'مساحة التخزين تقترب من الامتلاء',
        description: 'استخدام مساحة التخزين وصل إلى 85%، قد تحتاج إلى تنظيف أو توسيع المساحة قريباً.',
        impact: 'medium',
        timestamp: new Date(now.getTime() - 3 * 60 * 60 * 1000), // 3 hours ago
        dismissed: false,
        actionable: true,
        suggestions: [
          'حذف الملفات المؤقتة القديمة',
          'ضغط الصور المحفوظة',
          'أرشفة البيانات القديمة',
          'توسيع مساحة التخزين'
        ],
        metrics: {
          current: 85,
          threshold: 80,
          unit: '%'
        }
      },
      {
        id: '6',
        type: 'success',
        category: 'ai',
        title: 'تحسن في دقة Gemini API',
        description: 'دقة استخراج النصوص العربية من Gemini API تحسنت إلى 96% خلال آخر أسبوع.',
        impact: 'low',
        timestamp: new Date(now.getTime() - 6 * 60 * 60 * 1000), // 6 hours ago
        dismissed: false,
        actionable: false,
        suggestions: [
          'توثيق التحسينات المطبقة',
          'مشاركة أفضل الممارسات مع الفريق',
          'مراقبة الاستقرار في الأداء'
        ]
      }
    ];
  };

  const loadAlerts = async () => {
    setLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const smartAlerts = generateSmartAlerts();
      setAlerts(smartAlerts);
      
      // Send critical alerts as notifications
      smartAlerts
        .filter(alert => alert.type === 'critical' && !alert.dismissed)
        .forEach(alert => {
          addNotification({
            title: alert.title,
            message: alert.description,
            type: 'error'
          });
        });
      
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, dismissed: true } : alert
    ));
    
    addToast({
      title: 'تم إخفاء التنبيه',
      message: 'تم إخفاء التنبيه بنجاح',
      type: 'success',
      duration: 3000
    });
  };

  const getAlertIcon = (type: AlertType) => {
    switch (type) {
      case 'critical': return <AlertCircle className="w-5 h-5" />;
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      case 'info': return <Info className="w-5 h-5" />;
      case 'success': return <CheckCircle className="w-5 h-5" />;
      case 'prediction': return <Brain className="w-5 h-5" />;
    }
  };

  const getCategoryIcon = (category: AlertCategory) => {
    switch (category) {
      case 'performance': return <Zap className="w-4 h-4" />;
      case 'system': return <Activity className="w-4 h-4" />;
      case 'ai': return <Brain className="w-4 h-4" />;
      case 'business': return <Target className="w-4 h-4" />;
      case 'maintenance': return <Settings className="w-4 h-4" />;
    }
  };

  const getAlertStyles = (type: AlertType) => {
    switch (type) {
      case 'critical':
        return 'border-error-200 bg-error-50/50 text-error-800';
      case 'warning':
        return 'border-warning-200 bg-warning-50/50 text-warning-800';
      case 'info':
        return 'border-primary-200 bg-primary-50/50 text-primary-800';
      case 'success':
        return 'border-success-200 bg-success-50/50 text-success-800';
      case 'prediction':
        return 'border-purple-200 bg-purple-50/50 text-purple-800';
    }
  };

  const getImpactBadge = (impact: 'high' | 'medium' | 'low') => {
    const styles = {
      high: 'bg-error-100 text-error-700 border-error-200',
      medium: 'bg-warning-100 text-warning-700 border-warning-200',
      low: 'bg-success-100 text-success-700 border-success-200'
    };
    
    const labels = {
      high: 'عالي',
      medium: 'متوسط',
      low: 'منخفض'
    };

    return (
      <span className={`text-xs px-2 py-1 rounded-full border ${styles[impact]}`}>
        {labels[impact]}
      </span>
    );
  };

  const getTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `منذ ${hours} ساعة`;
    if (minutes > 0) return `منذ ${minutes} دقيقة`;
    return 'الآن';
  };

  const filteredAlerts = alerts.filter(alert => {
    if (dismissedFilter && !alert.dismissed) return false;
    if (!dismissedFilter && alert.dismissed) return false;
    if (categoryFilter !== 'all' && alert.category !== categoryFilter) return false;
    return true;
  });

  const criticalCount = alerts.filter(a => a.type === 'critical' && !a.dismissed).length;
  const warningCount = alerts.filter(a => a.type === 'warning' && !a.dismissed).length;

  if (loading) {
    return (
      <div className="card-gradient">
        <div className="card-header">
          <h3 className="font-bold text-gray-900">التنبيهات الذكية</h3>
        </div>
        <div className="card-body">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-gradient"
    >
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary-600" />
            التنبيهات الذكية
            {(criticalCount > 0 || warningCount > 0) && (
              <div className="flex items-center gap-1">
                {criticalCount > 0 && (
                  <span className="bg-error-500 text-white text-xs px-2 py-1 rounded-full">
                    {criticalCount} حرج
                  </span>
                )}
                {warningCount > 0 && (
                  <span className="bg-warning-500 text-white text-xs px-2 py-1 rounded-full">
                    {warningCount} تحذير
                  </span>
                )}
              </div>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as AlertCategory | 'all')}
              className="input text-sm"
            >
              <option value="all">جميع الفئات</option>
              <option value="performance">الأداء</option>
              <option value="system">النظام</option>
              <option value="ai">الذكاء الاصطناعي</option>
              <option value="business">العمل</option>
              <option value="maintenance">الصيانة</option>
            </select>
            <button
              onClick={() => setDismissedFilter(!dismissedFilter)}
              className={`text-sm px-3 py-1 rounded-lg transition-colors ${
                dismissedFilter ? 'bg-gray-200 text-gray-700' : 'bg-primary-100 text-primary-700'
              }`}
            >
              {dismissedFilter ? 'المخفية' : 'النشطة'}
            </button>
            <button onClick={loadAlerts} className="btn-outline text-sm">
              <RefreshCw className="w-4 h-4 ml-1" />
              تحديث
            </button>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="space-y-4 max-h-96 overflow-y-auto">
          <AnimatePresence>
            {filteredAlerts.length > 0 ? (
              filteredAlerts.map((alert, index) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.1 }}
                  className={`border rounded-xl p-4 ${getAlertStyles(alert.type)} ${
                    alert.dismissed ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getAlertIcon(alert.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          {getCategoryIcon(alert.category)}
                          <span className="font-medium capitalize">{alert.category}</span>
                        </div>
                        {getImpactBadge(alert.impact)}
                        <span className="text-xs text-gray-500">
                          {getTimeAgo(alert.timestamp)}
                        </span>
                      </div>
                      
                      <h4 className="font-semibold text-gray-900 mb-1">{alert.title}</h4>
                      <p className="text-sm opacity-90 mb-3">{alert.description}</p>
                      
                      {alert.metrics && (
                        <div className="mb-3 p-2 bg-white/60 rounded-lg">
                          <div className="flex items-center justify-between text-sm">
                            <span>القيمة الحالية:</span>
                            <span className="font-bold">
                              {alert.metrics.current} {alert.metrics.unit}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span>العتبة المسموحة:</span>
                            <span>{alert.metrics.threshold} {alert.metrics.unit}</span>
                          </div>
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  alert.type === 'critical' ? 'bg-error-500' :
                                  alert.type === 'warning' ? 'bg-warning-500' : 'bg-success-500'
                                }`}
                                style={{ 
                                  width: `${Math.min((alert.metrics.current / alert.metrics.threshold) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {alert.suggestions && alert.suggestions.length > 0 && (
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium">الإجراءات المقترحة:</h5>
                          <ul className="text-sm space-y-1">
                            {alert.suggestions.slice(0, 3).map((suggestion, idx) => (
                              <li key={idx} className="flex items-start gap-2 opacity-80">
                                <span className="text-primary-600 mt-1">•</span>
                                <span>{suggestion}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => dismissAlert(alert.id)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="إخفاء التنبيه"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>
                  {dismissedFilter ? 'لا توجد تنبيهات مخفية' : 'لا توجد تنبيهات نشطة'}
                </p>
                <p className="text-sm mt-1">النظام يعمل بشكل طبيعي</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default SmartAlerts;